import { Link } from "react-router";
import { Mail } from "lucide-react";
import { DELETE_ACCOUNT_EFFECTS, DELETE_ACCOUNT_PHRASE } from "@taskiq/shared";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHero, Section, SectionIntro } from "@/components/marketing/Section";

/**
 * The public, no-login-required page Google Play's Data safety section links
 * to for account deletion. Content is sourced from `DELETE_ACCOUNT_EFFECTS` -
 * the same disclosure the console's and the phone's delete-account dialogs
 * show - so this page cannot drift from what `delete_my_account()` actually
 * does.
 *
 * Deletion itself still requires signing in, which Play's policy allows: a
 * web resource must exist, but it may ask the same identity check the app
 * does. The mailto fallback below covers the one case that doesn't - someone
 * who has lost access to their account entirely.
 */
export function DeleteAccount() {
  return (
    <>
      <PageHero eyebrow="Account deletion" title="Delete your TaskIQ account" />

      <Section>
        <SectionIntro size="sm" eyebrow="How" title="Sign in, then delete it">
          <div className="company-copy">
            <p>
              Go to <strong>Profile → Delete your account</strong> on the web console or the mobile
              app, and type {DELETE_ACCOUNT_PHRASE} to confirm. This cannot be undone.
            </p>
          </div>
          <div className="intro-actions">
            <Link to="/login">
              <Button variant="primary" size="lg">
                Sign in to delete your account
              </Button>
            </Link>
          </div>
        </SectionIntro>
      </Section>

      <Section tone="sunken">
        <SectionIntro eyebrow="What happens" title="What is deleted, and what is kept" />

        <div className="card-grid">
          <Card padding="lg">
            <p className="eyebrow eyebrow--sm eyebrow--subtle">Deleted</p>
            <ul className="effects-list">
              {DELETE_ACCOUNT_EFFECTS.removed.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Card>

          <Card padding="lg">
            <p className="eyebrow eyebrow--sm eyebrow--subtle">Kept</p>
            <ul className="effects-list">
              {DELETE_ACCOUNT_EFFECTS.retained.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Card>
        </div>
      </Section>

      <Section id="contact" tone="panel" className="contact-band">
        <div className="split split--align-end">
          <SectionIntro
            tone="panel"
            eyebrow="Lost access?"
            title="Can't sign in to the account you want deleted?"
            lead="Email us from an address we can identify as yours. We'll verify ownership before deleting."
          />

          <a href="mailto:support@visionify.ai" className="contact-actions__link">
            <Button variant="panel" size="lg" block icon={<Mail />}>
              support@visionify.ai
            </Button>
          </a>
        </div>
      </Section>
    </>
  );
}
